import * as React from 'react'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import CloseIcon from '@mui/icons-material/Close'
import {
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks'

import FilterActiveImage from 'components/SimpleTable/assets/filter-active.png'
import FilterInactiveImage from 'components/SimpleTable/assets/filter-inactive.png'
import LeadTableLookupMenu from './LeadTableLookupFilterMenu'
import colors from 'lib/colors'

export type LeadTableLookupFilterValue = {
  operator: 'list'
  value: string[]
}

export type LeadTableLookupFilterGroup = {
  title: string
  keys: string[]
}

type LeadTableLookupFilterProps = {
  title: string
  lookup: Record<string, string>
  value?: LeadTableLookupFilterValue
  onChange: (value: LeadTableLookupFilterValue | undefined) => void
  filterExclusive?: boolean
  groups?: LeadTableLookupFilterGroup[]
}

const EMPTY_ARRAY: string[] = []

export default function LeadTableLookupFilter({
  title,
  lookup,
  value,
  onChange,
  filterExclusive = false,
  groups,
}: LeadTableLookupFilterProps) {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `lead-table-lookup-filter-${title}`,
  })

  const selectedKeys = value?.value ?? EMPTY_ARRAY
  const isActive = selectedKeys.length > 0

  const options = React.useMemo(() => {
    return Object.entries(lookup)
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, label]) => ({ key, label }))
  }, [lookup])

  const groupedOptions = React.useMemo(() => {
    if (!groups?.length) {
      return undefined
    }

    return groups.map((group) => ({
      title: group.title,
      options: group.keys
        .filter((key) => lookup[key])
        .map((key) => ({
          key,
          label: lookup[key],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    }))
  }, [groups, lookup])

  const handleToggle = React.useCallback(
    (key: string, checked: boolean) => {
      let nextSelectedKeys: string[]

      if (filterExclusive) {
        nextSelectedKeys = checked ? [key] : []
      } else if (checked) {
        nextSelectedKeys = Array.from(new Set([...selectedKeys, key]))
      } else {
        nextSelectedKeys = selectedKeys.filter((selectedKey) => selectedKey !== key)
      }

      if (nextSelectedKeys.length === 0) {
        onChange(undefined)
        return
      }

      onChange({
        operator: 'list',
        value: nextSelectedKeys,
      })
    },
    [filterExclusive, onChange, selectedKeys]
  )

  const handleClear = React.useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      event.stopPropagation()
      onChange(undefined)
    },
    [onChange]
  )

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        {...bindTrigger(popupState)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 'unset',
          position: 'relative',
          padding: '6px !important',
          borderColor: isActive ? colors.frenchBlue : undefined,
          '& .icon-layer': {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          '& .clear-icon': {
            display: 'none',
          },
          '&:hover .filter-icon': {
            display: isActive ? 'none' : undefined,
          },
          '&:hover .clear-icon': {
            display: isActive ? 'flex' : 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 18,
            height: 18,
            flexShrink: 0,
          }}
        >
          <Box className='icon-layer filter-icon'>
            <Box
              component='img'
              alt='inactive filter icon'
              src={FilterInactiveImage}
              width={16}
              height={16}
              sx={{ display: isActive ? 'none' : 'block', m:'1px' }}
            />
            <Box
              component='img'
              alt='active filter icon'
              src={FilterActiveImage}
              width={16}
              height={16}
              sx={{
                display: isActive ? 'block' : 'none',
                position: 'absolute',
                inset: 0,
                m:'1px'
              }}
            />
          </Box>

          <Box className='icon-layer clear-icon'>
            <Tooltip title='Clear Filter'>
              <CloseIcon
                onClick={handleClear}
                fontSize='medium'
                aria-label={`Clear Filter ${title}`}
              />
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            height: '18px',
            fontSize: '12px',
            lineHeight: '18px',
            color: isActive ? colors.frenchBlue : colors.slateGray,
          }}
        >
          {title}
        </Box>
      </Button>

      <LeadTableLookupMenu
        popupState={popupState}
        options={options}
        selectedKeys={selectedKeys}
        onToggle={handleToggle}
        groups={groupedOptions}
      />
    </>
  )
}
